import React, { useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import CheckFormWrapper from '../shared/CheckFormWrapper';
import FileUploadField from '../shared/FileUploadField';
import ColWithValuesInput from '../shared/ColWithValuesInput';
import TagsInput from '../shared/TagsInput';

/**
 * SensitiveTcodeCheckForm
 *
 * Checks which users have access to sensitive transaction codes and flags
 * exceptions based on their designation.
 *
 * API: POST /helper/sensitive-tcode-check
 *
 * Props:
 *   onSubmit   - (FormData) => void
 *   isLoading  - bool
 */
export default function SensitiveTcodeCheckForm({
  onSubmit,
  isLoading = false
}) {
  const [inputFile, setInputFile] = useState(null);
  const [usrAddrFile, setUsrAddrFile] = useState(null);

  const [userNameCol, setUserNameCol] = useState([]);
  const [userNameColError, setUserNameColError] = useState('');

  const [userTypeCol, setUserTypeCol] = useState('');
  const [userTypeVals, setUserTypeVals] = useState([]);

  const [userLockCol, setUserLockCol] = useState('');

  const [dateColName, setDateColName] = useState('');
  const [dateColVal, setDateColVal] = useState('');

  const [designationCol, setDesignationCol] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();

    if (userNameCol.length === 0) {
      setUserNameColError('Add at least one column name');
      return;
    }
    setUserNameColError('');

    const formData = new FormData();
    formData.append('inputFile', inputFile);
    formData.append('usrAddrFile', usrAddrFile);
    formData.append('userNameCol', JSON.stringify(userNameCol));
    formData.append(
      'userTypeCol',
      JSON.stringify(userTypeCol ? [userTypeCol, ...userTypeVals] : [])
    );
    formData.append('userLockCol', userLockCol);
    formData.append(
      'dateCol',
      JSON.stringify(dateColName && dateColVal ? [dateColName, dateColVal] : [])
    );
    formData.append('designationCol', designationCol);

    onSubmit(formData);
  };

  return (
    <CheckFormWrapper
      title="Sensitive TCode Check"
      description="Identifies users with access to sensitive transaction codes and flags exceptions based on their designation."
      onSubmit={handleSubmit}
      isLoading={isLoading}
    >
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <FileUploadField
          label="User / TCode Extract"
          file={inputFile}
          onChange={setInputFile}
          required
          helperText=".csv or .xlsx"
        />
        <FileUploadField
          label="User Address / HR File"
          file={usrAddrFile}
          onChange={setUsrAddrFile}
          required
          helperText=".csv or .xlsx"
        />
      </Box>

      <Box>
        <TagsInput
          label={
            <>
              User Name Column <span style={{ color: '#d32f2f' }}>*</span>
            </>
          }
          values={userNameCol}
          onChange={(next) => {
            setUserNameCol(next);
            if (next.length > 0) setUserNameColError('');
          }}
          placeholder="Column name (e.g. UserID), then Enter or Add"
          helperText="First item = source column in the extract. Add a second item only if you use a separate merge column."
        />
        {userNameColError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: 'block', mt: 0.5 }}
          >
            {userNameColError}
          </Typography>
        )}
      </Box>

      <ColWithValuesInput
        label="User Type Filter (optional)"
        colLabel="User Type Column"
        valuesLabel="Allowed User Types"
        colValue={userTypeCol}
        values={userTypeVals}
        onColChange={setUserTypeCol}
        onValuesChange={setUserTypeVals}
        helperText="Leave empty to skip this filter"
      />

      <TextField
        size="small"
        label="Lock Status Column (optional)"
        value={userLockCol}
        onChange={(e) => setUserLockCol(e.target.value)}
        sx={{ maxWidth: 320 }}
        helperText="Rows where this column value is blank are included; leave empty to skip"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="body2" fontWeight={500}>
          Date Filter (optional)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Date Column"
            value={dateColName}
            onChange={(e) => setDateColName(e.target.value)}
            sx={{ minWidth: 220, maxWidth: 280 }}
          />
          <TextField
            size="small"
            label="Comparison value"
            value={dateColVal}
            onChange={(e) => setDateColVal(e.target.value)}
            sx={{ minWidth: 220, maxWidth: 280 }}
            helperText="Use the same format as in your file (e.g. 2024-06-01)"
          />
        </Box>
      </Box>

      <TextField
        size="small"
        label="Designation Column"
        value={designationCol}
        onChange={(e) => setDesignationCol(e.target.value)}
        required
        sx={{ maxWidth: 320 }}
        helperText="Column in the merged result used to flag non-authorised designations"
      />
    </CheckFormWrapper>
  );
}
